import { Check, Star } from 'lucide-react';
import clsx from 'clsx';
import { SubscriptionPlan } from '../../types/plan';
import { useTranslation } from '../../hooks/useTranslation';
import { Button } from '../ui/Button';
import { formatCurrency } from '../../lib/formatters';

interface PlanSelectionCardProps {
  plan: SubscriptionPlan;
  isSelected: boolean;
  onSelectPlan: (planCode: string) => void;
  showCTA?: boolean;
  highlightRecommended?: boolean;
  isCurrentPlan?: boolean;
}

export const PlanSelectionCard = ({
  plan,
  isSelected,
  onSelectPlan,
  showCTA = true,
  highlightRecommended = false,
  isCurrentPlan = false,
}: PlanSelectionCardProps) => {
  const { t, currentLanguage } = useTranslation();

  const getPlanFeatures = () => {
    const features = [];

    // Public integrations
    const publicFeedsText = plan.maxPublicIntegration === -1 
      ? t('plans.features.unlimitedPublicFeeds')
      : t('plans.features.publicFeeds', { count: plan.maxPublicIntegration ?? 0 });
    features.push({ id: 'public-integrations', text: publicFeedsText });

    // Daily events
    const privateFeedsText = plan.dailyEventsLimit === -1 
      ? t('plans.features.unlimitedPrivateFeeds')
      : t('plans.features.privateFeeds', { count: plan.dailyEventsLimit ?? 0 });
    features.push({ id: 'daily-events', text: privateFeedsText });

    // Events
    const eventsText = plan.monthlyEventsLimit === -1 
      ? t('plans.features.unlimitedEvents')
      : t('plans.features.events', { count: (plan.monthlyEventsLimit ?? 0) });
    features.push({ id: 'monthly-events', text: eventsText });

    // Support
    const supportText = plan.supportEnabled 
      ? t('plans.features.prioritySupport')
      : t('plans.features.communitySupport');
    features.push({ id: 'support', text: supportText });

    // Retry
    const retryText = plan.retryEnabled 
      ? t('plans.features.automaticRetry')
      : t('plans.features.noRetry');
    features.push({ id: 'retry', text: retryText });

    return features;
  };

  const getCTAText = () => {
    if (isCurrentPlan) {
      return t('plans.cta.currentPlan');
    }
    if (plan.planCode === 'ENTERPRISE') {
      return t('plans.cta.contactSales');
    }
    return t('plans.cta.getStarted');
  };

  const getCTAVariant = () => {
    if (plan.planCode === 'ENTERPRISE') {
      return 'outline';
    }
    return isSelected ? 'primary' : 'outline';
  };

  const handleClick = () => {
    if (!isCurrentPlan) {
      onSelectPlan(plan.planCode);
    }
  };

  return (
    <div
      className={clsx(
        'relative rounded-lg border-2 transition-all duration-200',
        'bg-white shadow-sm hover:shadow-md',
        {
          'border-[var(--wl-primary)] ring-2 ring-[var(--wl-primary)] ring-opacity-20': isSelected,
          'border-gray-200': !isSelected,
          'transform scale-105': highlightRecommended,
        }
      )}
    >
      {/* Recommended Badge */}
      {highlightRecommended && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <div className="flex items-center gap-1 px-3 py-1 bg-[var(--wl-primary)] text-white text-xs font-semibold rounded-full shadow-md">
            <Star className="w-3 h-3 fill-current" />
            {t('plans.recommended')}
          </div>
        </div>
      )}

      <div className="p-6">
        {/* Plan Name */}
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          {plan.name}
        </h3>

        {/* Plan Description */}
        <p className="text-sm text-gray-600 mb-4">
          {plan.description}
        </p>

        {/* Pricing */}
        <div className="mb-6">
          {plan.planCode === 'ENTERPRISE' ? (
            <p className="text-lg font-semibold text-gray-900">
              {t('plans.pricing.contactUs')}
            </p>
          ) : (
            <div className="flex items-baseline gap-1">
              {plan.monthlyPrice === 0 ? (
                <span className="text-3xl font-bold text-gray-900">
                  {t('plans.pricing.free')}
                </span>
              ) : (
                <>
                  <span className="text-3xl font-bold text-gray-900">
                    {formatCurrency(plan.monthlyPrice, currentLanguage)}
                  </span>
                  <span className="text-sm text-gray-600">
                    {t('plans.pricing.perMonth')}
                  </span>
                </>
              )}
            </div>
          )}
          {plan.overagePrice > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              {formatCurrency(plan.overagePrice, currentLanguage)} {t('plans.pricing.perAdditionalEvent', 'per additional event')}
            </p>
          )}
        </div>

        {/* Features List */}
        <ul className="space-y-3 mb-6">
          {getPlanFeatures().map((feature) => (
            <li key={feature.id} className="flex items-start gap-2">
              <Check className="w-5 h-5 text-[var(--wl-primary)] flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700">{feature.text}</span>
            </li>
          ))}
        </ul>

        {/* CTA Button */}
        {showCTA && (
          <Button
            variant={getCTAVariant()}
            size="lg"
            className="w-full"
            onClick={handleClick}
            disabled={isCurrentPlan}
          >
            {getCTAText()}
          </Button>
        )}
      </div>
    </div>
  );
};
